import type { ReactNode } from 'react';
import type { GridSortModel, GridValidRowModel } from '@mui/x-data-grid';
import CrudPageLayout, {
  type CrudDeleteConfig,
  type PageVariantProps,
  type SectionVariantProps,
} from './CrudPageLayout';
import type {
  DataTableRowReorder,
  DataTableV2Column,
  DataTableV2Props,
} from './DataTableV2';
import DataTableV2 from './DataTableV2';
import FilterChips, { type FilterChipsProps } from './FilterChips';
import type { PageAddConfig } from './PageAdd';
import type { PageFilterConfig } from './PageFilter';
import type { PageSearchConfig } from './PageSearch';

type CrudListBodyProps<T extends GridValidRowModel> = {
  rows: T[];
  columns: DataTableV2Column<T>[];
  loading?: boolean;
  getRowId: DataTableV2Props<T>['getRowId'];
  getRowDetails?: DataTableV2Props<T>['getRowDetails'];
  onRowClick?: DataTableV2Props<T>['onRowClick'];
  contextMenuItems?: DataTableV2Props<T>['contextMenuItems'];
  searchConfig?: PageSearchConfig;
  filterConfig?: PageFilterConfig;
  filterChips?: FilterChipsProps;
  addConfig?: PageAddConfig;
  addEditDialog?: ReactNode;
  deleteConfig: CrudDeleteConfig<T>;
  rowReorder?: DataTableRowReorder;
  rowHeight?: number;
  paginationMode?: DataTableV2Props<T>['paginationMode'];
  rowCount?: DataTableV2Props<T>['rowCount'];
  paginationModel?: DataTableV2Props<T>['paginationModel'];
  onPaginationModelChange?: DataTableV2Props<T>['onPaginationModelChange'];
  pageSizeOptions?: DataTableV2Props<T>['pageSizeOptions'];
  sortingMode?: DataTableV2Props<T>['sortingMode'];
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
};

type CrudListPageProps<T extends GridValidRowModel> = CrudListBodyProps<T> &
  (PageVariantProps | SectionVariantProps);

const CrudListPage = <T extends GridValidRowModel>({
  rows,
  columns,
  loading,
  getRowId,
  getRowDetails,
  onRowClick,
  contextMenuItems,
  searchConfig,
  filterConfig,
  filterChips,
  addConfig,
  addEditDialog,
  deleteConfig,
  rowReorder,
  rowHeight,
  paginationMode,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  pageSizeOptions,
  sortingMode,
  sortModel,
  onSortModelChange,
  ...pageMeta
}: CrudListPageProps<T>) => {
  const serverPagination =
    paginationMode === 'server'
      ? {
          paginationMode,
          rowCount,
          paginationModel,
          onPaginationModelChange,
          pageSizeOptions,
        }
      : {};
  const serverSorting =
    sortingMode === 'server'
      ? {
          sortingMode,
          sortModel,
          onSortModelChange,
        }
      : {};

  return (
    <CrudPageLayout
      searchConfig={searchConfig}
      filterConfig={filterConfig}
      addConfig={addConfig}
      addEditDialog={addEditDialog}
      deleteConfig={deleteConfig}
      {...pageMeta}
    >
      {filterChips ? <FilterChips {...filterChips} /> : null}
      <DataTableV2<T>
        rows={rows}
        columns={columns}
        loading={loading}
        getRowId={getRowId}
        getRowDetails={getRowDetails}
        onRowClick={onRowClick}
        contextMenuItems={contextMenuItems}
        rowReorder={rowReorder}
        rowHeight={rowHeight}
        fillHeight
        {...serverPagination}
        {...serverSorting}
      />
    </CrudPageLayout>
  );
};

export default CrudListPage;
